import { uploadData, downloadData } from 'aws-amplify/storage';

const EMERGENCY_ROUTES_KEY = 'setting/emergency_routes_v2.json';

/**
 * Parse CSV file and convert to emergency route data
 * Format: SAFIELD001, SAUID, X, Y (separated by semicolon or comma)
 * Output: { "RouteName": [[["X", "Y"], ...], [["X", "Y"], ...]], ... }
 * where each inner array represents a SAUID group
 */
export const parseEmergencyRouteCSV = (csvText, fileName) => {
    // Split by newline and filter empty lines
    const lines = csvText.split(/\r?\n/).filter(line => line.trim());

    if (lines.length < 2) {
        throw new Error('CSVファイルが空です');
    }

    // Detect delimiter (semicolon or comma)
    const delimiter = lines[0].includes(';') ? ';' : ',';

    // Skip header line
    const dataLines = lines.slice(1);
    const routesMap = {};

    dataLines.forEach((line, index) => {
        // Split by detected delimiter
        const columns = line.split(delimiter).map(col => col.trim());

        if (columns.length >= 4) {
            const routeName = columns[0]; // SAFIELD001
            const sauid = columns[1]; // SAUID
            const x = columns[2]; // X
            const y = columns[3]; // Y

            if (!routeName || !sauid || !x || !y) {
                return;
            }

            // Initialize route if not exists
            if (!routesMap[routeName]) {
                routesMap[routeName] = {};
            }

            // Initialize SAUID group if not exists
            if (!routesMap[routeName][sauid]) {
                routesMap[routeName][sauid] = [];
            }

            // Add coordinate pair [Y, X] to the SAUID group
            routesMap[routeName][sauid].push([y, x]);
        } else {
            console.warn(`Skipping line ${index + 2}: insufficient columns (expected 4, got ${columns.length})`);
        }
    });

    // Convert the nested object structure to array structure
    // { "Route1": { "SAUID1": [[y,x], ...], "SAUID2": [[y,x], ...] } }
    // becomes { "Route1": [[[y,x], ...], [[y,x], ...]] }
    const result = {};
    Object.keys(routesMap).forEach(routeName => {
        result[routeName] = Object.values(routesMap[routeName]);
    });

    return result;
};

/**
 * Get emergency routes from S3
 */
export const apiGetEmergencyRoutes = async () => {
    try {
        const downloadResult = await downloadData({
            key: EMERGENCY_ROUTES_KEY,
            options: {
                level: 'public'
            }
        }).result;

        const blob = await downloadResult.body.blob();
        const text = await blob.text();
        const data = JSON.parse(text);
        return {
            success: true,
            data: data
        };
    } catch (error) {
        console.error('Error fetching emergency routes:', error);
        return {
            success: false,
            data: [],
            error: error.message
        };
    }
};/**
 * Save emergency routes to S3
 * New format: Array of route objects with full information
 * [
 *   {
 *     id: "route-1",
 *     fileName: "data.csv",
 *     routeName: "第二次緊急輸送路",
 *     displayOrder: 1,
 *     color: "#FF0000",
 *     coordinates: [["139.123", "35.456"], ...]
 *   }
 * ]
 */
export const apiSaveEmergencyRoutes = async (routesArray) => {
    try {
        const result = await uploadData({
            key: EMERGENCY_ROUTES_KEY,
            data: JSON.stringify(routesArray, null, 2),
            options: {
                level: 'public',
                contentType: 'application/json'
            }
        });
        // Wait for upload to complete
        await result.result;

        return {
            success: true
        };
    } catch (error) {
        console.error('Error saving emergency routes:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Upload CSV file and parse it
 */
export const apiUploadEmergencyRouteCSV = async (file) => {
    try {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = async (e) => {
                try {
                    const csvText = e.target.result;
                    const routesMap = parseEmergencyRouteCSV(csvText, file.name);

                    // Generate filename with timestamp in JST format: originalFileName_YYYYMMDDhhmmss.csv
                    const now = new Date();
                    // Convert to JST (UTC+9)
                    const jstDate = new Date(now.getTime() + (9 * 60 * 60 * 1000));
                    const year = jstDate.getUTCFullYear();
                    const month = String(jstDate.getUTCMonth() + 1).padStart(2, '0');
                    const day = String(jstDate.getUTCDate()).padStart(2, '0');
                    const hours = String(jstDate.getUTCHours()).padStart(2, '0');
                    const minutes = String(jstDate.getUTCMinutes()).padStart(2, '0');
                    const seconds = String(jstDate.getUTCSeconds()).padStart(2, '0');
                    const timestamp = `${year}${month}${day}${hours}${minutes}${seconds}`;

                    // Remove .csv extension from original filename and add timestamp
                    const baseFileName = file.name.replace(/\.csv$/i, '');
                    const formattedFileName = `${baseFileName}_${timestamp}.csv`;

                    resolve({
                        success: true,
                        routesMap: routesMap,
                        fileName: formattedFileName
                    });
                } catch (error) {
                    reject({
                        success: false,
                        error: error.message
                    });
                }
            };

            reader.onerror = () => {
                reject({
                    success: false,
                    error: 'ファイルの読み込みに失敗しました'
                });
            };

            reader.readAsText(file);
        });
    } catch (error) {
        console.error('Error uploading CSV:', error);
        return {
            success: false,
            error: error.message
        };
    }
};
